import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, Plus, DownloadSimple, UploadSimple, FileXls } from "@phosphor-icons/react";

export default function TimesheetAdmin() {
  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="timesheet-admin-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Timesheet Configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">Define tasks by role & KRA, submission rules, and who can log time. Clients are managed under Email-Domain Routing. The <Link to="/admin?tab=holiday-calendar" className="text-primary hover:underline" data-testid="ts-holiday-calendar-link">Holiday Calendar</Link> now lives under Admin and is shared with ITSM SLAs.</p>
      </div>
      <Tabs defaultValue="structure">
        <TabsList data-testid="ts-admin-tabs">
          <TabsTrigger value="structure" data-testid="ts-tab-structure">Roles · KRAs · Tasks</TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="ts-tab-exceptions">Exceptions</TabsTrigger>
          <TabsTrigger value="settings" data-testid="ts-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="structure"><StructureTab /></TabsContent>
        <TabsContent value="exceptions"><ExceptionsTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function StructureTab() {
  const [roles, setRoles] = useState([]);
  const [kras, setKras] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [kraId, setKraId] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newKra, setNewKra] = useState("");
  const [newTask, setNewTask] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const loadRoles = useCallback(async () => { const { data } = await api.get("/timesheet/job-roles"); setRoles(data); }, []);
  const loadKras = useCallback(async (rid) => {
    if (!rid) return setKras([]);
    const { data } = await api.get("/timesheet/kras", { params: { job_role_id: rid } }); setKras(data);
  }, []);
  const loadTasks = useCallback(async (kid) => {
    if (!kid) return setTasks([]);
    const { data } = await api.get("/timesheet/tasks", { params: { kra_id: kid } }); setTasks(data);
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);
  useEffect(() => { loadKras(roleId); setKraId(""); setTasks([]); }, [roleId, loadKras]);
  useEffect(() => { loadTasks(kraId); }, [kraId, loadTasks]);

  const addRole = async () => { if (!newRole.trim()) return; await api.post("/timesheet/job-roles", { name: newRole }); setNewRole(""); loadRoles(); toast.success("Role added"); };
  const delRole = async (id) => { await api.delete(`/timesheet/job-roles/${id}`); if (roleId === id) setRoleId(""); loadRoles(); toast.success("Role removed"); };
  const addKra = async () => { if (!newKra.trim() || !roleId) return; await api.post("/timesheet/kras", { job_role_id: roleId, name: newKra }); setNewKra(""); loadKras(roleId); toast.success("KRA added"); };
  const delKra = async (id) => { await api.delete(`/timesheet/kras/${id}`); if (kraId === id) setKraId(""); loadKras(roleId); toast.success("KRA removed"); };
  const addTask = async () => { if (!newTask.trim() || !kraId) return; await api.post("/timesheet/tasks", { kra_id: kraId, name: newTask }); setNewTask(""); loadTasks(kraId); toast.success("Task added"); };
  const delTask = async (id) => { await api.delete(`/timesheet/tasks/${id}`); loadTasks(kraId); toast.success("Task removed"); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/timesheet/structure/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "timesheet-structure-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/timesheet/structure/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data.created;
      toast.success(`Imported: ${c.job_roles} roles · ${c.kras} KRAs · ${c.tasks} tasks added`);
      loadRoles();
      if (roleId) { loadKras(roleId); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-primary/20" data-testid="ts-import-card">
        <div className="flex items-start gap-3">
          <FileXls size={28} className="text-rag-green-fg shrink-0 mt-0.5" />
          <div>
            <div className="font-display">Bulk import from Excel</div>
            <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">Upload an .xlsx/.csv with columns <b>Job Role · KRA · Task</b>. Existing names are reused — only missing rows are created.</div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={downloadTemplate} data-testid="ts-download-template-btn" className="rounded-sm text-xs"><DownloadSimple size={14} className="mr-2" />Sample template</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" onChange={doImport} className="hidden" data-testid="ts-import-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="ts-import-btn" className="rounded-sm text-xs"><UploadSimple size={14} className="mr-2" />{importing ? "Importing…" : "Import Excel"}</Button>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
      {/* Roles */}
      <Card className="p-4" data-testid="ts-roles-col">
        <div className="font-display mb-3">Job Roles</div>
        <div className="flex gap-2 mb-3">
          <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="e.g. Support Engineer" data-testid="ts-new-role-input" />
          <Button size="icon" onClick={addRole} data-testid="ts-add-role-btn"><Plus size={16} /></Button>
        </div>
        <div className="space-y-1">
          {roles.map((r) => (
            <div key={r.id} className={`flex items-center justify-between px-2 py-1.5 rounded-sm cursor-pointer text-sm ${roleId === r.id ? "bg-accent/50" : "hover:bg-accent/30"}`}
              onClick={() => setRoleId(r.id)} data-testid={`ts-role-${r.id}`}>
              <span>{r.name}</span>
              <Trash size={14} className="text-muted-foreground hover:text-rag-red-fg dark:hover:text-rag-red-fg" onClick={(e) => { e.stopPropagation(); delRole(r.id); }} />
            </div>
          ))}
        </div>
      </Card>
      {/* KRAs */}
      <Card className="p-4" data-testid="ts-kras-col">
        <div className="font-display mb-3">KRAs {roleId ? "" : <span className="text-xs text-muted-foreground">(pick a role)</span>}</div>
        {roleId && (
          <div className="flex gap-2 mb-3">
            <Input value={newKra} onChange={(e) => setNewKra(e.target.value)} placeholder="e.g. Customer Support" data-testid="ts-new-kra-input" />
            <Button size="icon" onClick={addKra} data-testid="ts-add-kra-btn"><Plus size={16} /></Button>
          </div>
        )}
        <div className="space-y-1">
          {kras.map((k) => (
            <div key={k.id} className={`flex items-center justify-between px-2 py-1.5 rounded-sm cursor-pointer text-sm ${kraId === k.id ? "bg-accent/50" : "hover:bg-accent/30"}`}
              onClick={() => setKraId(k.id)} data-testid={`ts-kra-${k.id}`}>
              <span>{k.name}</span>
              <Trash size={14} className="text-muted-foreground hover:text-rag-red-fg dark:hover:text-rag-red-fg" onClick={(e) => { e.stopPropagation(); delKra(k.id); }} />
            </div>
          ))}
        </div>
      </Card>
      {/* Tasks */}
      <Card className="p-4" data-testid="ts-tasks-col">
        <div className="font-display mb-3">Tasks {kraId ? "" : <span className="text-xs text-muted-foreground">(pick a KRA)</span>}</div>
        {kraId && (
          <div className="flex gap-2 mb-3">
            <Input value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="e.g. Ticket Handling" data-testid="ts-new-task-input" />
            <Button size="icon" onClick={addTask} data-testid="ts-add-task-btn"><Plus size={16} /></Button>
          </div>
        )}
        <div className="space-y-1">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-2 py-1.5 rounded-sm text-sm hover:bg-accent/30" data-testid={`ts-task-${t.id}`}>
              <span>{t.name}</span>
              <Trash size={14} className="text-muted-foreground hover:text-rag-red-fg dark:hover:text-rag-red-fg cursor-pointer" onClick={() => delTask(t.id)} />
            </div>
          ))}
        </div>
      </Card>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({ submission_cutoff_hours: 24, reminder_enabled: true, reminder_hour_ist: 23, digest_enabled: true, digest_hour_ist: 9, notify_from_email: "" });
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/timesheet/settings").then((r) => setS(r.data)); }, []);
  const upd = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const save = async () => {
    await api.put("/timesheet/settings", {
      submission_cutoff_hours: Number(s.submission_cutoff_hours),
      reminder_enabled: !!s.reminder_enabled, reminder_hour_ist: Number(s.reminder_hour_ist),
      digest_enabled: !!s.digest_enabled, digest_hour_ist: Number(s.digest_hour_ist),
      notify_from_email: s.notify_from_email || "",
    });
    toast.success("Settings saved");
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/timesheet/test-email");
      if (data.status === "sent") toast.success("Test email sent — check your inbox");
      else if (data.status === "skipped") toast.error("Microsoft 365 mail is not configured");
      else toast.error(`Send failed: ${data.detail || "unknown error"}`);
    } catch { toast.error("Could not send test email"); }
    finally { setTesting(false); }
  };
  const hourLabel = (h) => { const ap = h < 12 ? "AM" : "PM"; const hr = h % 12 || 12; return `${hr}:00 ${ap} IST`; };
  return (
    <div className="mt-4 max-w-lg space-y-4" data-testid="ts-settings-tab">
      <Card className="p-5 space-y-3">
        <div className="font-display">Submission Window</div>
        <div>
          <Label className="text-xs">Cutoff hours after day end</Label>
          <Input type="number" min={0} value={s.submission_cutoff_hours} onChange={(e) => upd({ submission_cutoff_hours: e.target.value })} className="w-40" data-testid="ts-cutoff-input" />
          <p className="text-xs text-muted-foreground mt-1.5">
            Users can submit a day's timesheet up to this many hours after the day ends (midnight UTC). Set 0 to require same-day submission.
          </p>
        </div>
      </Card>

      <Card className="p-5 space-y-4" data-testid="ts-notify-settings">
        <div className="font-display">Email Notifications</div>
        <p className="text-xs text-muted-foreground -mt-2">Sent from your Microsoft 365 mailbox via Microsoft Graph. Times are in IST.</p>

        <div>
          <Label className="text-xs">Send from (Microsoft 365 mailbox)</Label>
          <Input type="email" value={s.notify_from_email || ""} onChange={(e) => upd({ notify_from_email: e.target.value })}
            placeholder="helpdesk@procainconsulting.com" data-testid="ts-notify-from-input" />
          <p className="text-xs text-muted-foreground mt-1.5">A mailbox in your tenant the app can send as (Mail.Send). Defaults to your helpdesk mailbox.</p>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <div>
            <div className="text-sm font-medium">Daily user reminder</div>
            <div className="text-xs text-muted-foreground">Emails users who haven't submitted today.</div>
          </div>
          <Switch checked={!!s.reminder_enabled} onCheckedChange={(v) => upd({ reminder_enabled: v })} data-testid="ts-reminder-toggle" />
        </div>
        {s.reminder_enabled && (
          <div>
            <Label className="text-xs">Reminder time (IST)</Label>
            <Select value={String(s.reminder_hour_ist)} onValueChange={(v) => upd({ reminder_hour_ist: Number(v) })}>
              <SelectTrigger className="w-44" data-testid="ts-reminder-hour"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-3">
          <div>
            <div className="text-sm font-medium">Daily manager digest</div>
            <div className="text-xs text-muted-foreground">Summary of the team's submissions from the previous day.</div>
          </div>
          <Switch checked={!!s.digest_enabled} onCheckedChange={(v) => upd({ digest_enabled: v })} data-testid="ts-digest-toggle" />
        </div>
        {s.digest_enabled && (
          <div>
            <Label className="text-xs">Digest time (IST)</Label>
            <Select value={String(s.digest_hour_ist)} onValueChange={(v) => upd({ digest_hour_ist: Number(v) })}>
              <SelectTrigger className="w-44" data-testid="ts-digest-hour"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="border-t border-border pt-3">
          <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="ts-send-test-btn">
            {testing ? "Sending…" : "Send test email to me"}
          </Button>
        </div>
      </Card>

      <Button onClick={save} data-testid="ts-save-settings-btn">Save settings</Button>
    </div>
  );
}


function ExceptionsTab() {
  const [exempt, setExempt] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pick, setPick] = useState("");

  const load = async () => {
    const [ex, all] = await Promise.all([
      api.get("/timesheet/exceptions"),
      api.get("/timesheet/users"),
    ]);
    setExempt(ex.data);
    setAllUsers(all.data.users || []);
  };
  useEffect(() => { load(); }, []);

  const exemptIds = new Set(exempt.map((u) => u.id));
  const candidates = allUsers.filter((u) => !exemptIds.has(u.id));

  const add = async () => {
    if (!pick) return;
    await api.post("/timesheet/exceptions", { user_id: pick, exempt: true });
    setPick(""); load(); toast.success("Added to exceptions");
  };
  const remove = async (uid) => { await api.delete(`/timesheet/exceptions/${uid}`); load(); toast.success("Removed from exceptions"); };

  return (
    <div className="mt-4 max-w-2xl space-y-4" data-testid="ts-exceptions-tab">
      <Card className="p-5 space-y-4">
        <div>
          <div className="font-display">Timesheet Exceptions</div>
          <p className="text-xs text-muted-foreground mt-1">People who need not fill timesheets. They are hidden from the team dashboard and reports, and never receive reminders.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">Add a person</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="ts-exception-picker"><SelectValue placeholder="Select a user…" /></SelectTrigger>
              <SelectContent>
                {candidates.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={!pick} data-testid="ts-add-exception-btn"><Plus size={16} className="mr-1.5" />Add</Button>
        </div>
        <div className="border-t border-border pt-3 space-y-1">
          {exempt.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-2 py-2 rounded-sm text-sm hover:bg-accent/30" data-testid={`ts-exception-${u.id}`}>
              <span>{u.name} <span className="text-muted-foreground text-xs">· {u.email}</span></span>
              <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`ts-remove-exception-${u.id}`} className="text-rag-red-fg h-7 text-xs">Remove</Button>
            </div>
          ))}
          {!exempt.length && <div className="text-xs text-muted-foreground py-4 text-center">No exceptions — everyone with access is expected to fill timesheets.</div>}
        </div>
      </Card>
    </div>
  );
}
